import {
  VStack,
  Center,
  useColorModeValue,
  HStack,
  Heading,
  Image,
  Text,
  Popover,
  useDisclosure,
  PopoverArrow,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Stack,
  useMediaQuery,
  IconButton,
  Box,
  Flex,
  Button
} from '@chakra-ui/react'
import { WhatsappContact } from 'components'
import { Property } from 'generated/graphql'

import { FcApproval } from 'react-icons/fc'
import React from 'react'
import { FiMapPin, FiPhone } from 'react-icons/fi'
import { VscArrowLeft, VscBriefcase } from 'react-icons/vsc'
import { useHistory } from 'react-router'
import { ContactAgent, ContactAgentForm, FavoriteProperty } from '..'
import { useScrollTo } from 'hooks'
import { iconSizes } from 'theme'
import { useTranslation } from 'react-i18next'
import { useAuthContext } from 'context/AuthProvider'
import { SecondaryButton } from 'components/Buttons'

const Form: React.FC<{
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  property: Property
  contactRef: React.RefObject<HTMLDivElement>
}> = ({ isOpen, onClose, onOpen, property, contactRef }) => {
  const [isTabletOrMobile] = useMediaQuery('(max-width: 40em)')
  const scroll = useScrollTo(contactRef)
  const { t } = useTranslation()
  const bg = useColorModeValue('gray.100', 'gray.700')
  const color = useColorModeValue('gray.700', 'gray.100')
  return isTabletOrMobile ? (
    <SecondaryButton
      minW={{ base: '100%', md: 'fit-content' }}
      fontWeight="extrabold"
      fontSize="x-small"
      height={12}
      rounded="2xl"
      bg={bg}
      border="none"
      color={color}
      _hover={{
        backgroundColor: 'gray.50',
        borderColor: 'gray.200'
      }}
      onClick={() => scroll()}
      leftIcon={<FiPhone />}
    >
      <Text fontSize="xs">{t('property_details.contact_agent')}</Text>
    </SecondaryButton>
  ) : (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-end" closeOnBlur>
      <PopoverTrigger>
        <Button
          minW={{ base: '100%', md: 'fit-content' }}
          fontWeight="extrabold"
          fontSize="x-small"
          height={12}
          rounded="2xl"
          bg={bg}
          border="none"
          color={color}
          _hover={{
            backgroundColor: 'gray.50',
            borderColor: 'gray.200'
          }}
        >
          {t('property_details.contact_agent')}
        </Button>
      </PopoverTrigger>
      <PopoverContent p={5} minW={{ base: '100vw', md: '30vw' }}>
        <PopoverArrow />
        <PopoverCloseButton />

        <VStack spacing={8} my={8} alignItems="flex-start">
          <HStack alignItems="flex-start" w="100%">
            <ContactAgent property={property as Property} />
            <WhatsappContact propertyData={property as Property} />
          </HStack>
          <ContactAgentForm onClose={onClose} property={property as Property} />
        </VStack>
      </PopoverContent>
    </Popover>
  )
}
type HeaderProps = {
  property: Property
  loading: boolean
  contactRef: React.RefObject<HTMLDivElement>
}
const Header: React.FC<HeaderProps> = ({ property, loading, contactRef }) => {
  const history = useHistory()
  const { isAuthenticated } = useAuthContext()
  const state = history.location.state as any
  const { isOpen, onClose, onOpen } = useDisclosure()
  const { t } = useTranslation()
  const [isTabletOrMobile] = useMediaQuery('(max-width: 40em)')
  return (
    <VStack
      align="flex-start"
      position="relative"
      pt={[4, 6]}
      pb={2}
      _after={{
        content: '""',
        width: '100vw',
        zIndex: -1,
        left: '50%',
        top: 0,
        transform: 'translateX(-50%)',
        position: 'absolute',
        borderBottom: '1px solid',
        borderColor: useColorModeValue('gray.50', 'gray.700'),
        height: '100%',
        bg: useColorModeValue('white', 'gray.800')
      }}
      mb={4}
    >
      <Center width="100%" mb={isTabletOrMobile ? 0 : 1} justifyContent="space-between">
        <Skeleton isLoaded={!loading}>
          <Center
            cursor="pointer"
            onClick={() =>
              history.push(isAuthenticated ? '/auth/' : '/', {
                quickSearch: state?.quickSearch
              })
            }
          >
            <Skeleton isLoaded={!loading}>
              <IconButton
                aria-label="back"
                p={2}
                variant="ghost"
                border="1px solid"
                borderColor={useColorModeValue('gray.100', 'gray.600')}
                borderRadius="full"
                size="sm"
                as={VscArrowLeft}
              />
            </Skeleton>
            <Skeleton mx={2} isLoaded={!loading}>
              <Text
                fontSize="xs"
                fontWeight="extrabold"
                pointerEvents="none"
                _hover={{ color: 'gray.300' }}
                color={useColorModeValue('gray.600', 'gray.400')}
              >
                {t('property_details.back')}
              </Text>
            </Skeleton>
          </Center>
        </Skeleton>
        <Skeleton isLoaded={!loading}>
          <FavoriteProperty withLabel propertyData={property as Property} />
        </Skeleton>
      </Center>
      <Stack
        spacing={6}
        direction={{ base: 'column', md: 'row' }}
        align={{ base: 'flex-start', lg: 'center' }}
        justify="space-between"
        width="100%"
        py={2}
      >
        <HStack alignItems="flex-start" width={{ base: '100%', md: 'fit-content' }} spacing={4}>
          <Skeleton borderRadius="xl" isLoaded={!loading}>
            <Image
              boxSize="40px"
              borderRadius="xl"
              draggable={false}
              objectFit="cover"
              src={property?.agent?.photo?.formats?.thumbnail?.url}
              alt="nsoko property agent picture"
            />
          </Skeleton>
          <Box align="flex-start">
            <Center justifyContent="flex-start" mb={1}>
              <Skeleton isLoaded={!loading}>
                <Heading size="xs">{property?.agent?.name ?? 'Nsoko property'}</Heading>
              </Skeleton>
              {property?.agent?.verified && (
                <Center mx={2}>
                  <Skeleton isLoaded={!loading} borderRadius="full">
                    <FcApproval size={iconSizes.lg} />
                  </Skeleton>
                  <Skeleton isLoaded={!loading} mx={1}>
                    <Text color="green.500" fontWeight="extrabold" fontSize="x-small">
                      {t('property_details.verified')}
                    </Text>
                  </Skeleton>
                </Center>
              )}
            </Center>
            <Skeleton isLoaded={!loading}>
              <Box
                direction={{ base: 'column', md: 'row' }}
                align={{ base: 'flex-start' }}
                color={useColorModeValue('gray.600', 'gray.400')}
                fontSize="xs"
              >
                {property?.agent?.agency?.name && (
                  <Flex align="center" mb={1}>
                    <VscBriefcase size={iconSizes.md} />
                    <Text fontSize="x-small" mx={1}>
                      {property?.agent?.agency?.name}
                    </Text>
                  </Flex>
                )}
                <Flex align="center">
                  <FiMapPin size={iconSizes.md} />
                  <Text fontSize="x-small" mx={1}>
                    {property?.suburb?.city?.name}
                  </Text>
                </Flex>
              </Box>
            </Skeleton>
          </Box>
        </HStack>
        <Stack
          spacing={4}
          width={{ base: '100%', md: 'fit-content' }}
          direction={{ base: 'column', md: 'row' }}
        >
          <Skeleton isLoaded={!loading}>
            <Form
              contactRef={contactRef}
              isOpen={isOpen}
              property={property}
              onClose={onClose}
              onOpen={onOpen}
            />
          </Skeleton>
          <Skeleton isLoaded={!loading}>
            <WhatsappContact m={0} propertyData={property as Property} />
          </Skeleton>
        </Stack>
      </Stack>
    </VStack>
  )
}

export default Header
